/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

'use client';

import { getSession, signIn } from 'next-auth/react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { useTranslation } from '@/lib/i18n';
import { logger } from '@/lib/logger';
import { Input, Select } from '@/themes/cray26/ui';

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

/**
 * Cray26 Theme - Business Login Page
 * Login form requiring customer_type for business customers
 */

export default function LoginBusinessPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [customerType, setCustomerType] = useState<'RETAIL' | 'WHOLESALE'>('RETAIL');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl') || '/';
  const message = searchParams.get('message') || '';
  const messageType = searchParams.get('type') || 'info';

  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await signIn('credentials', {
        email,
        password,
        customer_type: customerType,
        redirect: false,
      });

      if (result?.error) {
        if (result.error === 'INACTIVE_REGULAR') {
          setError(t('auth.inactiveRegular'));
        } else if (result.error === 'INACTIVE_BUSINESS') {
          setError(t('auth.inactiveBusiness'));
        } else {
          setError(result.error || t('common.error'));
        }
        logger.error({ msg: 'Business login failed', error: result.error });
      } else {
        const session = await getSession();
        if (session) {
          logger.info({ msg: 'Business login successful' });
          router.push(callbackUrl);
        } else {
          setError(t('common.error'));
        }
      }
    } catch (err) {
      setError(t('common.error'));
      logger.error({ msg: 'Business login error', error: err });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t('auth.signIn')} ({t('auth.business')})
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.noAccount')}{' '}
          <Link href="/register-business" className="font-medium text-blue-600 hover:text-blue-500">
            {t('auth.registerBusiness')}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.regularLoginPrompt')}{' '}
          <Link href="/login" className="font-medium text-blue-600 hover:text-blue-500">
            {t('auth.login')}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">{error}</div>}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth.email')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={t('auth.emailPlaceholder')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth.password')}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={t('auth.passwordPlaceholder')}
                />
              </div>
            </div>

            <Select
              label={t('auth.customerType')}
              value={customerType}
              onChange={(val) => setCustomerType(val as 'RETAIL' | 'WHOLESALE')}
              options={[
                { value: 'RETAIL', label: t('auth.reseller') || 'Retail' },
                { value: 'WHOLESALE', label: t('auth.wholesaler') || 'Wholesale' },
              ]}
              placeholder={t('auth.selectCustomerType') || 'Select customer category'}
              required
            />

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('common.loading') : t('auth.signIn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
